import { useCallback } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';

import { useKibanaLogger } from '../../../shared/hooks/kibana-logger/kibana-logger';
import { dataBuilderStore } from '../data-builder.store';
import type { BuildPayloadInput } from './build-verify-payload';
import {
  type BuildVerifyPayloadResult,
  buildVerifyPayload,
  buildVerifyPayloadWithStateMerge,
  ROW_LIMIT_PROBE_STATE_MERGE,
} from './build-verify-payload';
import type { DataBuilderSchema } from './data-builder-api.types';

export function useBuildDataBuilderPayload(
  input: Pick<BuildPayloadInput, 'client' | 'name'> & {
    datasets?: BuildPayloadInput['datasets'];
    rowLimitValidationProbe?: boolean;
  }
) {
  const { kibanaLogger } = useKibanaLogger();
  const { isRevelioAdmin } = useIsRevelioAdmin();

  const build = useCallback(
    (
      schema: DataBuilderSchema,
      instanceIds?: ReadonlySet<string>
    ): BuildVerifyPayloadResult | null => {
      const state = dataBuilderStore.getState();

      // Non-admins never control database/schema/bucket — the backend fills
      // them from the client's settings. Strip them here (not only in the
      // delivery forms) so values hydrated from a resumed, duplicated, or
      // shared run can't reach the payload as a stale delivery location.
      const snowflakeConfig =
        state.snowflakeConfig === null || isRevelioAdmin
          ? state.snowflakeConfig
          : { table: state.snowflakeConfig.table };
      const s3Config =
        state.s3Config === null || isRevelioAdmin
          ? state.s3Config
          : { path: state.s3Config.path, format: state.s3Config.format };

      try {
        const payloadInput = {
          schema,
          state: {
            datasetInstances: state.datasetInstances,
            configByInstance: state.configByInstance,
            lagData: state.lagData,
            deliveryMethod: state.deliveryMethod,
            deliveryFormat: state.deliveryFormat,
            snowflakeConfig,
            s3Config,
            modelVersions: state.modelVersions,
          },
          client: input.client,
          name: input.name,
          datasets: input.datasets,
          instanceIds,
        };

        if (input.rowLimitValidationProbe === true) {
          return buildVerifyPayloadWithStateMerge(
            payloadInput,
            ROW_LIMIT_PROBE_STATE_MERGE
          );
        }

        return buildVerifyPayload(payloadInput);
      } catch (_error: unknown) {
        kibanaLogger?.error(
          `Data Builder: payload build failed. ${_error instanceof Error ? _error.message : String(_error)}`
        );
        return null;
      }
    },
    [
      input.client,
      input.datasets,
      input.name,
      input.rowLimitValidationProbe,
      isRevelioAdmin,
      kibanaLogger,
    ]
  );

  return { build };
}
